import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Layout, Typography, Form, Input, Select, Button, message, Space, Avatar, Upload } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { UserOutlined, LogoutOutlined, UploadOutlined } from '@ant-design/icons';
import './Profile.css';

const { Header, Content } = Layout;
const { Title } = Typography;

const Profile = () => {
  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      gender: '',
      householdType: '',
      street: '',
      city: '',
      zipCode: '',
      county: '',
      state: '',
      country: 'United States',
      housingStatus: '',
      profilePicture: '',
      veteranStatus: '',
      veteranService: '',
      branchOfMilitary: '',
      militaryOperations: '',
      militaryDischargeStatus: '',
      militaryServiceStart: '',
      militaryServiceEnd: '',
      supportSystem: '',
      recordType: '',
      housingLossCity: '',
      housingLossZipCode: '',
      mostTimeLocation: '',
      connectToServices: '',
      preferredHousingResources: '',
      cityToReceiveServices: '',
    },
  });

  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const veteranStatus = watch('veteranStatus');
  const veteranService = watch('veteranService');
  const supportSystem = watch('supportSystem');
  const connectToServices = watch('connectToServices');

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await fetch('/backend/get_profile.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        const data = await response.json();
        console.log('Données reçues de get_profile.php:', JSON.stringify(data, null, 2));

        if (response.ok && !data.error) {
          const normalizedData = {
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            gender: data.gender || '',
            householdType: data.householdType || '',
            street: data.street || '',
            city: data.city || '',
            zipCode: data.zipCode || '',
            county: data.county || '',
            state: data.state || '',
            country: data.country || 'United States',
            housingStatus: data.housingStatus || '',
            profilePicture: data.profilePicture || '',
            veteranStatus: data.veteranStatus || '',
            veteranService: data.veteranService || '',
            branchOfMilitary: data.branchOfMilitary || '',
            militaryOperations: data.militaryOperations || '',
            militaryDischargeStatus: data.militaryDischargeStatus || '',
            militaryServiceStart: data.militaryServiceStart || '',
            militaryServiceEnd: data.militaryServiceEnd || '',
            supportSystem: data.supportSystem || '',
            recordType: data.recordType || '',
            housingLossCity: data.housingLossCity || '',
            housingLossZipCode: data.housingLossZipCode || '',
            mostTimeLocation: data.mostTimeLocation || '',
            connectToServices: data.connectToServices || '',
            preferredHousingResources: data.preferredHousingResources || '',
            cityToReceiveServices: data.cityToReceiveServices || '',
          };

          console.log('Données normalisées pour reset:', JSON.stringify(normalizedData, null, 2));
          reset(normalizedData);
          setPreviewImage(data.profilePicture || null);
        } else {
          message.error(data.error || 'Erreur lors de la récupération des données du profil.');
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        message.error('Une erreur s\'est produite lors de la récupération des données.');
      }
    };

    fetchProfileData();
  }, [reset]);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'profilePicture' && data[key] && data[key].file) {
          formData.append(key, data[key].file);
        } else {
          formData.append(key, data[key] || '');
        }
      });

      const response = await fetch('/backend/update_profile.php', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        message.success('Profil sauvegardé avec succès !');
        if (result.profilePicture) {
          setPreviewImage(result.profilePicture);
        }
      } else {
        message.error(result.error || 'Erreur lors de la mise à jour du profil.');
      }
    } catch (error) {
      message.error('Une erreur s\'est produite lors de la mise à jour du profil.');
    }
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('/backend/logout.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        message.success('Déconnexion réussie');
        navigate('/login');
      } else {
        message.error(data.error || 'Erreur lors de la déconnexion.');
      }
    } catch (error) {
      message.error('Erreur lors de la déconnexion.');
    }
  };

  const handleRemoveProfilePicture = async () => {
    try {
      const response = await fetch('/backend/update_profile.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ profilePicture: 'remove' }),
      });

      const result = await response.json();

      if (response.ok) {
        message.success('Photo de profil supprimée avec succès !');
        setPreviewImage(null);
        reset(prev => ({ ...prev, profilePicture: '' }));
      } else {
        message.error(result.error || 'Erreur lors de la suppression de la photo de profil.');
      }
    } catch (error) {
      message.error('Erreur lors de la suppression de la photo.');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <Header
        style={{
          background: '#fff',
          padding: '0 30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid #e5e7eb',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={() => setHoveredCard('nav')}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <Space align="center">
          <Avatar
            src={previewImage}
            icon={<UserOutlined />}
            style={{ backgroundColor: '#2b6cb0', marginRight: 12, transition: 'transform 0.3s ease' }}
            className={hoveredCard === 'nav' ? 'nav-avatar-hover' : ''}
          />
          <Typography.Title
            level={4}
            style={{ margin: 0, color: '#2b6cb0', fontWeight: 600, transition: 'color 0.3s ease' }}
            className={hoveredCard === 'nav' ? 'nav-title-hover' : ''}
          >
            NH Guide
          </Typography.Title>
        </Space>
        <div style={{ flex: 1 }} />
        <Space size={20}>
          <Link
            to="/home"
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
          >
            Home
          </Link>
          <Link
            to="/profile"
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
          >
            Profile
          </Link>
          <Link
            to="/services-resources"
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
          >
            Services and Resources
          </Link>
          <Link
            to="/privacy-data-sharing"
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
          >
            Privacy and Data Sharing
          </Link>
          <Button
            type="link"
            icon={<LogoutOutlined />}
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#e74c3c')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Space>
      </Header>

      <Content style={{ padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
        <div className="profile-container">
          <Title level={3} style={{ color: '#333', textAlign: 'center', marginBottom: 20 }}>
            User Profile
          </Title>
          <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
            <Form.Item label="Profile Picture" style={{ marginBottom: '20px', textAlign: 'center' }}>
              <Avatar
                src={previewImage}
                icon={<UserOutlined />}
                size={100}
                style={{ marginBottom: 10 }}
              />
              <Controller
                name="profilePicture"
                control={control}
                render={({ field }) => (
                  <Upload
                    beforeUpload={() => false}
                    onChange={({ file }) => {
                      field.onChange({ file });
                      if (file) {
                        const url = URL.createObjectURL(file);
                        setPreviewImage(url);
                      }
                    }}
                    accept="image/*"
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>Upload Photo</Button>
                  </Upload>
                )}
              />
              {previewImage && (
                <Button
                  type="link"
                  style={{ color: '#e74c3c', marginTop: 10 }}
                  onClick={handleRemoveProfilePicture}
                >
                  Remove Photo
                </Button>
              )}
            </Form.Item>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <Form.Item label="First Name" style={{ flex: 1 }}>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{ required: 'First Name is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="First Name"
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.firstName && <span style={{ color: 'red' }}>{errors.firstName.message}</span>}
              </Form.Item>
              <Form.Item label="Last Name" style={{ flex: 1 }}>
                <Controller
                  name="lastName"
                  control={control}
                  rules={{ required: 'Last Name is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Last Name"
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.lastName && <span style={{ color: 'red' }}>{errors.lastName.message}</span>}
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <Form.Item label="Gender" style={{ flex: 1 }}>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: 'Gender is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select gender"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' },
                        { value: 'Non-binary', label: 'Non-binary' },
                        { value: 'Trans-female', label: 'Trans-female' },
                        { value: 'Trans-male', label: 'Trans-male' },
                        { value: 'Culturally Specific Gender Identity', label: 'Culturally Specific Gender Identity (e.g., Two-Spirit)' },
                        { value: 'Prefer-not-to-answer', label: 'Prefer not to answer' },
                      ]}
                    />
                  )}
                />
                {errors.gender && <span style={{ color: 'red' }}>{errors.gender.message}</span>}
              </Form.Item>
              <Form.Item label="Race and Ethnicity" style={{ flex: 1 }}>
                <Controller
                  name="householdType"
                  control={control}
                  rules={{ required: 'Race and Ethnicity is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select Race and Ethnicity"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'American Indian, Alaska Native, or Indigenous', label: 'American Indian, Alaska Native, or Indigenous' },
                        { value: 'Asian or Asian American', label: 'Asian or Asian American' },
                        { value: 'Black, African American, or African', label: 'Black, African American, or African' },
                        { value: 'Hispanic/Latina/e/o', label: 'Hispanic/Latina/e/o' },
                        { value: 'Middle Eastern or North African', label: 'Middle Eastern or North African' },
                        { value: 'Native Hawaiian or Pacific Islander', label: 'Native Hawaiian or Pacific Islander' },
                        { value: 'White', label: 'White' },
                        { value: 'Prefer not to', label: 'Prefer not to' },
                      ]}
                    />
                  )}
                />
                {errors.householdType && <span style={{ color: 'red' }}>{errors.householdType.message}</span>}
              </Form.Item>
            </div>

            <Form.Item label="Current Location" style={{ marginBottom: '20px' }}>
              <Controller
                name="street"
                control={control}
                rules={{ required: 'Street is required' }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Street number and name or cross-streets"
                    style={{ marginBottom: '20px' }}
                    onChange={(e) => field.onChange(e.target.value)}
                    value={field.value}
                  />
                )}
              />
              {errors.street && <span style={{ color: 'red' }}>{errors.street.message}</span>}
              <div style={{ display: 'flex', gap: '20px' }}>
                <Controller
                  name="city"
                  control={control}
                  rules={{ required: 'City is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="City"
                      style={{ flex: 1 }}
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.city && <span style={{ color: 'red' }}>{errors.city.message}</span>}
                <Controller
                  name="zipCode"
                  control={control}
                  rules={{ required: 'Zip Code is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Zip Code"
                      style={{ flex: 1 }}
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.zipCode && <span style={{ color: 'red' }}>{errors.zipCode.message}</span>}
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <Controller
                  name="county"
                  control={control}
                  rules={{ required: 'County is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="County"
                      style={{ flex: 1 }}
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.county && <span style={{ color: 'red' }}>{errors.county.message}</span>}
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: 'State is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select state"
                      style={{ flex: 1 }}
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'AL', label: 'Alabama' },
                        { value: 'AK', label: 'Alaska' },
                        { value: 'AZ', label: 'Arizona' },
                        { value: 'AR', label: 'Arkansas' },
                        { value: 'CA', label: 'California' },
                        { value: 'CO', label: 'Colorado' },
                        { value: 'CT', label: 'Connecticut' },
                        { value: 'DE', label: 'Delaware' },
                        { value: 'FL', label: 'Florida' },
                        { value: 'GA', label: 'Georgia' },
                        { value: 'HI', label: 'Hawaii' },
                        { value: 'ID', label: 'Idaho' },
                        { value: 'IL', label: 'Illinois' },
                        { value: 'IN', label: 'Indiana' },
                        { value: 'IA', label: 'Iowa' },
                        { value: 'KS', label: 'Kansas' },
                        { value: 'KY', label: 'Kentucky' },
                        { value: 'LA', label: 'Louisiana' },
                        { value: 'ME', label: 'Maine' },
                        { value: 'MD', label: 'Maryland' },
                        { value: 'MA', label: 'Massachusetts' },
                        { value: 'MI', label: 'Michigan' },
                        { value: 'MN', label: 'Minnesota' },
                        { value: 'MS', label: 'Mississippi' },
                        { value: 'MO', label: 'Missouri' },
                        { value: 'MT', label: 'Montana' },
                        { value: 'NE', label: 'Nebraska' },
                        { value: 'NV', label: 'Nevada' },
                        { value: 'NH', label: 'New Hampshire' },
                        { value: 'NJ', label: 'New Jersey' },
                        { value: 'NM', label: 'New Mexico' },
                        { value: 'NY', label: 'New York' },
                        { value: 'NC', label: 'North Carolina' },
                        { value: 'ND', label: 'North Dakota' },
                        { value: 'OH', label: 'Ohio' },
                        { value: 'OK', label: 'Oklahoma' },
                        { value: 'OR', label: 'Oregon' },
                        { value: 'PA', label: 'Pennsylvania' },
                        { value: 'RI', label: 'Rhode Island' },
                        { value: 'SC', label: 'South Carolina' },
                        { value: 'SD', label: 'South Dakota' },
                        { value: 'TN', label: 'Tennessee' },
                        { value: 'TX', label: 'Texas' },
                        { value: 'UT', label: 'Utah' },
                        { value: 'VT', label: 'Vermont' },
                        { value: 'VA', label: 'Virginia' },
                        { value: 'WA', label: 'Washington' },
                        { value: 'WV', label: 'West Virginia' },
                        { value: 'WI', label: 'Wisconsin' },
                        { value: 'WY', label: 'Wyoming' },
                      ]}
                    />
                  )}
                />
                {errors.state && <span style={{ color: 'red' }}>{errors.state.message}</span>}
              </div>
              <Controller
                name="country"
                control={control}
                rules={{ required: 'Country is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select country"
                    style={{ marginTop: '10px' }}
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                    options={[
                      { value: 'United States', label: 'United States' },
                      { value: 'Canada', label: 'Canada' },
                      { value: 'Mexico', label: 'Mexico' },
                      { value: 'United Kingdom', label: 'United Kingdom' },
                    ]}
                  />
                )}
              />
              {errors.country && <span style={{ color: 'red' }}>{errors.country.message}</span>}
            </Form.Item>

            <Form.Item label="Housing Status" style={{ marginBottom: '20px' }}>
              <Controller
                name="housingStatus"
                control={control}
                rules={{ required: 'Housing Status is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select housing status"
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                    options={[
                      { value: 'Unhoused and staying in an automobile', label: 'Unhoused and staying in an automobile' },
                      { value: 'Unhoused and staying in a place not meant for human habitation', label: 'Unhoused and staying in a place not meant for human habitation' },
                      { value: 'Unhoused and staying temporarily with someone', label: 'Unhoused and staying temporarily with someone' },
                      { value: 'Unhoused and in an emergency shelter/transitional housing', label: 'Unhoused and in an emergency shelter/transitional housing' },
                      { value: 'Still housed but will be unhoused in less than 7 days', label: 'Still housed but will be unhoused in less than 7 days' },
                      { value: 'Still housed but will be unhoused in less than 30 days', label: 'Still housed but will be unhoused in less than 30 days' },
                      { value: 'Still housed but will be unhoused in 30 - 60 days', label: 'Still housed but will be unhoused in 30 - 60 days' },
                    ]}
                  />
                )}
              />
              {errors.housingStatus && <span style={{ color: 'red' }}>{errors.housingStatus.message}</span>}
            </Form.Item>

            <Form.Item label="Veteran Status" style={{ marginBottom: '20px' }}>
              <Controller
                name="veteranStatus"
                control={control}
                rules={{ required: 'Veteran Status is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select veteran status"
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                    options={[
                      { value: 'Yes', label: 'Yes' },
                      { value: 'No', label: 'No' },
                      { value: 'Prefer not to answer', label: 'Prefer not to answer' },
                    ]}
                  />
                )}
              />
              {errors.veteranStatus && <span style={{ color: 'red' }}>{errors.veteranStatus.message}</span>}
            </Form.Item>

            {veteranStatus === 'Yes' && (
              <Form.Item label="Veteran Service" style={{ marginBottom: '20px' }}>
                <Controller
                  name="veteranService"
                  control={control}
                  rules={{ required: 'Veteran Service is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select veteran service"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'Branch of Military', label: 'Branch of Military' },
                        { value: 'Military Service Start (date)', label: 'Military Service Start (date)' },
                        { value: 'Military Service End (date)', label: 'Military Service End (date)' },
                        { value: 'Military Operations', label: 'Military Operations' },
                        { value: 'Military Discharge Status', label: 'Military Discharge Status' },
                      ]}
                    />
                  )}
                />
                {errors.veteranService && <span style={{ color: 'red' }}>{errors.veteranService.message}</span>}
              </Form.Item>
            )}

            {veteranStatus === 'Yes' && veteranService === 'Branch of Military' && (
              <Form.Item label="Branch of Military" style={{ marginBottom: '20px' }}>
                <Controller
                  name="branchOfMilitary"
                  control={control}
                  rules={{ required: 'Branch of Military is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select branch of military"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'Air Force', label: 'Air Force' },
                        { value: 'Army', label: 'Army' },
                        { value: 'Coast Guard', label: 'Coast Guard' },
                        { value: 'Marines', label: 'Marines' },
                        { value: 'Navy', label: 'Navy' },
                        { value: 'Space Force', label: 'Space Force' },
                      ]}
                    />
                  )}
                />
                {errors.branchOfMilitary && <span style={{ color: 'red' }}>{errors.branchOfMilitary.message}</span>}
              </Form.Item>
            )}

            {veteranStatus === 'Yes' && veteranService === 'Military Operations' && (
              <Form.Item label="Military Operations" style={{ marginBottom: '20px' }}>
                <Controller
                  name="militaryOperations"
                  control={control}
                  rules={{ required: 'Military Operations is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select military operations"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'Iraq (New Dawn)', label: 'Iraq (New Dawn)' },
                        { value: 'Iraq (Iraqi Freedom)', label: 'Iraq (Iraqi Freedom)' },
                        { value: 'Afghanistan', label: 'Afghanistan' },
                        { value: 'Persian Gulf War (Operation Desert Storm) 1990 - 1991', label: 'Persian Gulf War (Operation Desert Storm) 1990 - 1991' },
                        { value: 'Vietnam War', label: 'Vietnam War' },
                        { value: 'Korean War', label: 'Korean War' },
                        { value: 'World War II', label: 'World War II' },
                        { value: 'Other Operations', label: 'Other Operations' },
                        { value: 'Prefer not to answer', label: 'Prefer not to answer' },
                      ]}
                    />
                  )}
                />
                {errors.militaryOperations && <span style={{ color: 'red' }}>{errors.militaryOperations.message}</span>}
              </Form.Item>
            )}

            {veteranStatus === 'Yes' && veteranService === 'Military Discharge Status' && (
              <Form.Item label="Military Discharge Status" style={{ marginBottom: '20px' }}>
                <Controller
                  name="militaryDischargeStatus"
                  control={control}
                  rules={{ required: 'Military Discharge Status is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select military discharge status"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'Honorable', label: 'Honorable' },
                        { value: 'Other than Honorable Conditions', label: 'Other than Honorable Conditions' },
                        { value: 'Dishonorable', label: 'Dishonorable' },
                        { value: 'Prefer not to answer', label: 'Prefer not to answer' },
                      ]}
                    />
                  )}
                />
                {errors.militaryDischargeStatus && <span style={{ color: 'red' }}>{errors.militaryDischargeStatus.message}</span>}
              </Form.Item>
            )}

            {veteranStatus === 'Yes' && veteranService === 'Military Service Start (date)' && (
              <Form.Item label="Military Service Start (date)" style={{ marginBottom: '20px' }}>
                <Controller
                  name="militaryServiceStart"
                  control={control}
                  rules={{ required: 'Military Service Start date is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="YYYY-MM-DD"
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.militaryServiceStart && <span style={{ color: 'red' }}>{errors.militaryServiceStart.message}</span>}
              </Form.Item>
            )}

            {veteranStatus === 'Yes' && veteranService === 'Military Service End (date)' && (
              <Form.Item label="Military Service End (date)" style={{ marginBottom: '20px' }}>
                <Controller
                  name="militaryServiceEnd"
                  control={control}
                  rules={{ required: 'Military Service End date is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="YYYY-MM-DD"
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.militaryServiceEnd && <span style={{ color: 'red' }}>{errors.militaryServiceEnd.message}</span>}
              </Form.Item>
            )}

            <Form.Item label="Support System" style={{ marginBottom: '20px' }}>
              <Controller
                name="supportSystem"
                control={control}
                rules={{ required: 'Support System is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select support system"
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                    options={[
                      { value: 'Record type', label: 'Record type' },
                      { value: 'None', label: 'None' },
                    ]}
                  />
                )}
              />
              {errors.supportSystem && <span style={{ color: 'red' }}>{errors.supportSystem.message}</span>}
            </Form.Item>

            {supportSystem === 'Record type' && (
              <Form.Item label="Record Type" style={{ marginBottom: '20px' }}>
                <Controller
                  name="recordType"
                  control={control}
                  rules={{ required: 'Record Type is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select record type"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      options={[
                        { value: 'HMIS', label: 'HMIS' },
                        { value: 'Other', label: 'Other' },
                      ]}
                    />
                  )}
                />
                {errors.recordType && <span style={{ color: 'red' }}>{errors.recordType.message}</span>}
              </Form.Item>
            )}

            <Form.Item label="Where did you lose your housing?" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '20px' }}>
                <Controller
                  name="housingLossCity"
                  control={control}
                  rules={{ required: 'City is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="City"
                      style={{ flex: 1 }}
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.housingLossCity && <span style={{ color: 'red' }}>{errors.housingLossCity.message}</span>}
                <Controller
                  name="housingLossZipCode"
                  control={control}
                  rules={{ required: 'Zip Code is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Zip Code"
                      style={{ flex: 1 }}
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value}
                    />
                  )}
                />
                {errors.housingLossZipCode && <span style={{ color: 'red' }}>{errors.housingLossZipCode.message}</span>}
              </div>
            </Form.Item>

            <Form.Item label="Where are you located most time?" style={{ marginBottom: '20px' }}>
              <Controller
                name="mostTimeLocation"
                control={control}
                rules={{ required: 'Location is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select location"
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                    options={[
                      { value: 'San Francisco, 94102', label: 'San Francisco, 94102' },
                      { value: 'New York, 10001', label: 'New York, 10001' },
                      { value: 'Houston, 77002', label: 'Houston, 77002' },
                      { value: 'Los Angeles, 90001', label: 'Los Angeles, 90001' },
                      { value: 'Chicago, 60601', label: 'Chicago, 60601' },
                    ]}
                  />
                )}
              />
              {errors.mostTimeLocation && <span style={{ color: 'red' }}>{errors.mostTimeLocation.message}</span>}
            </Form.Item>

            <Form.Item label="Would you like to be connected to services and support resources in or near that area?" style={{ marginBottom: '20px' }}>
              <Controller
                name="connectToServices"
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select option"
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                    options={[
                      { value: 'Yes', label: 'Yes' },
                      { value: 'No', label: 'No' },
                    ]}
                  />
                )}
              />
              {errors.connectToServices && <span style={{ color: 'red' }}>{errors.connectToServices.message}</span>}
            </Form.Item>

            {connectToServices === 'No' && (
              <Form.Item label="Where would you like to be connected to any available housing crisis resources?" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <Controller
                    name="preferredHousingResources"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Preferred Housing Resources"
                        style={{ flex: 1 }}
                        onChange={(e) => field.onChange(e.target.value)}
                        value={field.value}
                      />
                    )}
                  />
                  <Controller
                    name="cityToReceiveServices"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="City to Receive Services"
                        style={{ flex: 1 }}
                        onChange={(e) => field.onChange(e.target.value)}
                        value={field.value}
                      />
                    )}
                  />
                </div>
              </Form.Item>
            )}

            <Form.Item style={{ textAlign: 'center' }}>
              <Button type="primary" htmlType="submit" className="save-button">
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Content>
    </Layout>
  );
};

export default Profile;